import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IStepOption, TourService, TourState } from 'ngx-ui-tour-md-menu';
import { take } from 'rxjs';
import { AppTourWelcomeDialogComponent } from './app-tour-welcome-dialog.component';

/** Persistencia: el onboarding inicial ya se ofreció (Iniciar / Ahora no). */
export const APP_TOUR_OFFERED_STORAGE_KEY = 'radiopharm.tour.app.offered';

@Injectable({ providedIn: 'root' })
export class AppTourService {
    private readonly tourService = inject(TourService);
    private readonly translate = inject(TranslateService);
    private readonly dialog = inject(MatDialog);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);

    private endSubscriptionActive = false;
    private offerInFlight = false;
    /** Solo true cuando el usuario pulsa Finalizar en el último paso. */
    private completeRequested = false;
    /** Consumido por DoseCalculatorTourService.maybeAutoStart() tras navegar. */
    private doseTourHandoffPending = false;

    /** True si el diálogo de bienvenida ya se ofreció (aceptado o rechazado). */
    isOffered(): boolean {
        try {
            return localStorage.getItem(APP_TOUR_OFFERED_STORAGE_KEY) === '1';
        } catch {
            return false;
        }
    }

    markOffered(): void {
        try {
            localStorage.setItem(APP_TOUR_OFFERED_STORAGE_KEY, '1');
        } catch {
            // private mode / quota — no bloquear la UI
        }
    }

    /**
     * Primer acceso al área privada: diálogo de consentimiento.
     * Nunca inicia el tour sin confirmación.
     */
    maybeOfferWelcome(): void {
        if (this.isOffered() || this.offerInFlight) return;
        if (this.tourService.getStatus() === TourState.ON) return;
        if (this.dialog.openDialogs.length > 0) return;

        this.offerInFlight = true;
        this.translate
            .get('tour.app.welcome.title')
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.isOffered() || this.tourService.getStatus() === TourState.ON) {
                    this.offerInFlight = false;
                    return;
                }
                if (this.dialog.openDialogs.length > 0) {
                    this.offerInFlight = false;
                    return;
                }

                const ref = this.dialog.open(AppTourWelcomeDialogComponent, {
                    width: '400px',
                    disableClose: true
                });

                ref.afterClosed()
                    .pipe(take(1), takeUntilDestroyed(this.destroyRef))
                    .subscribe((result: 'start' | 'dismiss' | undefined) => {
                        this.offerInFlight = false;
                        this.markOffered();
                        if (result === 'start') {
                            queueMicrotask(() => this.start());
                        }
                    });
            });
    }

    /** Relanzar desde Perfil (ignora offered). */
    restart(): void {
        this.start();
    }

    start(): void {
        if (this.tourService.getStatus() === TourState.ON) return;
        if (this.dialog.openDialogs.length > 0) return;

        this.completeRequested = false;
        this.ensureEndPersistence();
        this.tourService.initialize(this.buildSteps(), this.stepDefaults());
        this.tourService.start();
    }

    /** Omitir / ESC / cierre de menú: cierra sin handoff a Dose Calculator. */
    skip(): void {
        this.completeRequested = false;
        this.tourService.end();
    }

    /** Finalizar en el último paso: cierra y navega a Dose Calculator. */
    finish(): void {
        this.completeRequested = true;
        this.tourService.end();
    }

    /**
     * Dose Calculator llama esto tras registrar anchors.
     * @returns true solo si el tour general terminó con Finalizar.
     */
    consumeDoseTourHandoff(): boolean {
        const pending = this.doseTourHandoffPending;
        this.doseTourHandoffPending = false;
        return pending;
    }

    /** Orden = menú lateral (Administración es opcional si no eres admin). */
    readonly anchorIds = [
        'app-nav-dose-calculator',
        'app-nav-history',
        'app-nav-protocols',
        'app-nav-subscription',
        'app-nav-account',
        'app-nav-profile',
        'app-nav-admin',
        'app-nav-notifications'
    ] as const;

    private ensureEndPersistence(): void {
        if (this.endSubscriptionActive) return;
        this.endSubscriptionActive = true;

        this.tourService.end$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.markOffered();
                if (!this.completeRequested) return;
                this.completeRequested = false;
                this.doseTourHandoffPending = true;
                void this.router.navigateByUrl('/dose-calculator');
            });
    }

    private stepDefaults(): IStepOption {
        const t = (key: string) => this.translate.instant(key);
        return {
            enableBackdrop: true,
            disablePageScrolling: false,
            smoothScroll: true,
            centerAnchorOnScroll: true,
            showProgress: true,
            showArrow: true,
            isOptional: false,
            duplicateAnchorHandling: 'registerLast',
            // Abre a la derecha del rail (evita pegar el popover al borde izquierdo).
            placement: {
                horizontal: true,
                xPosition: 'after',
                yPosition: 'below'
            },
            backdropConfig: {
                offset: 0,
                backgroundColor: 'transparent'
            },
            popoverClass: 'app-tour-popover',
            prevBtnTitle: t('tour.app.buttons.prev'),
            nextBtnTitle: t('tour.app.buttons.next'),
            endBtnTitle: t('tour.app.buttons.end'),
            stepDimensions: {
                minWidth: '280px',
                maxWidth: '360px',
                width: 'auto'
            }
        };
    }

    private buildSteps(): IStepOption[] {
        const step = (
            anchorId: string,
            key: string,
            options: Pick<IStepOption, 'isOptional'> = {}
        ): IStepOption => ({
            anchorId,
            stepId: anchorId,
            title: this.translate.instant(`tour.app.${key}.title`),
            content: this.translate.instant(`tour.app.${key}.content`),
            isOptional: options.isOptional ?? false
        });

        return [
            step('app-nav-dose-calculator', 'doseCalculator'),
            step('app-nav-history', 'history'),
            step('app-nav-protocols', 'protocols'),
            step('app-nav-subscription', 'subscription'),
            step('app-nav-account', 'account'),
            step('app-nav-profile', 'profileNav'),
            step('app-nav-admin', 'admin', { isOptional: true }),
            step('app-nav-notifications', 'notifications')
        ];
    }
}
